import { useRef, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  Box,
  Flex,
  Icon,
  Select as ChakraSelect,
  Text,
} from '@chakra-ui/react';

import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import { buildTimezoneOptions } from '../utils/passHelpers';
import InfoBanner from '../../../common/InfoBanner';
import { CustomInfoIcon } from '../../../../theme/icons/CustomInfoIcon';
import { CustomCalendarIcon } from '../../../../theme/icons/CustomCalendarIcon';
import { CustomClockIcon } from '../../../../theme/icons/CustomClockIcon';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const formatDisplayDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-GB', { month: 'long' });
  const year = String(d.getFullYear()).slice(-2);
  return `${day} ${month}, ${year}`;
};

const formatDisplayTime = (timeStr: string) =>
  timeStr.replace(':', '.');

const isTimePast = (time: string, hour: number, minute: number) => {
  const [h, m] = time.split(':').map(Number);
  return h < hour || (h === hour && m <= minute);
};

const buildTimeOptions = (isToday: boolean, hour: number, minute: number) => {
  const options: { value: string; label: string; disabled: boolean }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const disabled = isToday && (h < hour || (h === hour && m <= minute));
      options.push({ value, label: value, disabled });
    }
  }
  return options;
};

const getDaysUntilExpiry = (expiryDate: string, todayStr: string) => {
  const diff =
    new Date(expiryDate + 'T00:00:00').getTime() -
    new Date(todayStr + 'T00:00:00').getTime();
  return diff <= 0 ? 0 : Math.round(diff / MS_PER_DAY);
};

// Shared styles for the label cell on the left side of each row
const LABEL_CELL_PROPS = {
  bg: 'white',
  borderLeftRadius: '3px',
  h: '31px',
  w: '127px',
  flexShrink: 0,
  align: 'center' as const,
  px: '12px',
  ml: '2px',
};

// Shared styles for each row container
const ROW_PROPS = {
  bg: 'rgba(228, 228, 228, 0.5)',
  borderRadius: '5px',
  h: '35px',
  overflow: 'hidden' as const,
  align: 'center' as const,
};

interface PassExpirySectionProps {
  expiryDate?: string | null;
  expiryTime?: string | null;
  expiryTimezone?: string;
  isCreate?: boolean;
  hasExistingExpiry?: boolean;
  errors?: Record<string, string>;
  onExpiryDateChange?: (date: string) => void;
  onExpiryTimeChange?: (time: string) => void;
  onExpiryTimezoneChange?: (timezone: string) => void;
  onFieldFocus?: (identifier: string) => void;
}

const PassExpirySection = ({
  expiryDate,
  expiryTime,
  expiryTimezone,
  isCreate,
  hasExistingExpiry,
  errors = {},
  onExpiryDateChange,
  onExpiryTimeChange,
  onExpiryTimezoneChange,
  onFieldFocus,
}: PassExpirySectionProps) => {
  const dateInputRef = useRef<HTMLInputElement>(null);

  const tz = expiryTimezone || 'UTC';
  const nowInTz = useMemo(() => moment().tz(tz), [tz]);
  const todayStr = nowInTz.format('YYYY-MM-DD');
  const currentHour = nowInTz.hour();
  const currentMinute = nowInTz.minute();
  const isToday = expiryDate === todayStr;

  const timeOptions = buildTimeOptions(isToday, currentHour, currentMinute);
  const daysUntilExpiry = expiryDate
    ? getDaysUntilExpiry(expiryDate, todayStr)
    : null;

  const handleDateChange = (val: string) => {
    if (!val && hasExistingExpiry) return;
    onExpiryDateChange?.(val);
    if (
      val === todayStr &&
      expiryTime &&
      isTimePast(expiryTime, currentHour, currentMinute)
    ) {
      onExpiryTimeChange?.('');
    }
  };

  const handleTimeChange = (val: string) => {
    if (!val && hasExistingExpiry) return;
    onExpiryTimeChange?.(val);
  };

  return (
    <SectionWrapper
      title="Pass expiry"
      subtitle={
        <Text fontSize="15px" color="grey">
            Best for transit, ID or access passes. If a pass still has marketing
            value, consider disabling scanning instead.
        </Text>
      }
      openOnLoad
      id="pass-expiry-section"
      tooltip="Set a date and time to automatically archive this pass."
    >
      <Box display="flex" flexDirection="column" gap={4}>
        <Text textStyle="bodyFamilyRegular" fontSize="16px" color="black">
          Set expiry date and time
        </Text>

        {/* DATE ROW */}
        
          <input
            ref={dateInputRef}
            type="date"
            value={expiryDate || ''}
            min={todayStr}
            onFocus={() => onFieldFocus?.('expiryDate')}
            onChange={(e) => handleDateChange(e.target.value)}
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
          />
          <Flex
            {...ROW_PROPS}
            cursor="pointer"
            onClick={() => {
              onFieldFocus?.('expiryDate');
              dateInputRef.current?.showPicker?.();
            }}
          >
            <Flex {...LABEL_CELL_PROPS}>
              <Text textStyle="bodyFamilyRegular" fontSize="14px" color="black">
                Date
              </Text>
            </Flex>
            {expiryDate ? (
              <Flex align="center" gap={2} px="14px" flex={1}>
                <Icon as={CustomCalendarIcon} boxSize="18px" color="#FF4935" />
                <Text
                  textStyle="bodyFamilyRegular"
                  fontSize="14px"
                  color="black"
                >
                  {formatDisplayDate(expiryDate)}
                </Text>
              </Flex>
            ) : (
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="14px"
                color="#989898"
                px="14px"
              >
                Select date
              </Text>
            )}
          </Flex>
        

        {/* TIME ROW */}
        
          <Flex
            {...ROW_PROPS}
            opacity={expiryDate ? 1 : 0.5}
            pointerEvents={expiryDate ? 'auto' : 'none'}
            position="relative"
          >
            <Flex {...LABEL_CELL_PROPS}>
              <Text textStyle="bodyFamilyRegular" fontSize="14px" color="black">
                Time
              </Text>
            </Flex>
            {expiryTime ? (
              <Flex
                align="center"
                gap={2}
                px="14px"
                flex={1}
                pointerEvents="none"
              >
                <Icon as={CustomClockIcon} boxSize="18px" color="#FF4935" />
                <Text
                  textStyle="bodyFamilyRegular"
                  fontSize="14px"
                  color="black"
                >
                  {formatDisplayTime(expiryTime)}
                </Text>
              </Flex>
            ) : (
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="14px"
                color="#989898"
                px="14px"
                pointerEvents="none"
              >
                Select time
              </Text>
            )}
            {expiryDate && (
              <ChakraSelect
                variant="unstyled"
                position="absolute"
                inset={0}
                opacity={0}
                cursor="pointer"
                h="full"
                icon={<></>}
                value={expiryTime || ''}
                onFocus={() => onFieldFocus?.('expiryTime')}
                onChange={(e) => handleTimeChange(e.target.value)}
              >
                <option value="">Select time</option>
                {timeOptions
                  .filter((opt) => !opt.disabled)
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </ChakraSelect>
            )}
          </Flex>
        

        {errors.expiryTime && (
          <Flex align="center" gap={1.5} pl={2} mt={-2}>
            <CustomErrorIcon boxSize="12px" color="#E53E3E" />
            <Text fontSize="12px" color="#E53E3E">
              {errors.expiryTime}
            </Text>
          </Flex>
        )}

        {/* TIMEZONE ROW */}
        <Flex
          {...ROW_PROPS}
          opacity={expiryDate ? 1 : 0.5}
          pointerEvents={expiryDate ? 'auto' : 'none'}
        >
          <Flex {...LABEL_CELL_PROPS}>
            <Text textStyle="bodyFamilyRegular" fontSize="14px" color="black">
              Timezone
            </Text>
          </Flex>
          <ChakraSelect
            variant="unstyled"
            textStyle="bodyFamilyRegular"
            fontSize="14px"
            color={expiryTimezone ? 'black' : '#989898'}
            px="10px"
            h="full"
            value={expiryTimezone || ''}
            onChange={(e) => onExpiryTimezoneChange?.(e.target.value)}
          >
            {buildTimezoneOptions(expiryDate ? new Date(expiryDate + 'T12:00:00') : undefined).map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </ChakraSelect>
        </Flex>

        {daysUntilExpiry != null && (
          <Text textStyle="bodyFamilyRegular" fontSize="14px" color="#989898">
            {daysUntilExpiry === 0
              ? 'This pass will expire today.'
              : `This pass will expire in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'day' : 'days'}.`}
          </Text>
        )}

        {!hasExistingExpiry && expiryDate && expiryTime && (
          <InfoBanner
            color="#FD5F12"
            bg="#FFEEE7"
            icon={<CustomErrorIcon boxSize="20px" color="#FD5F12" />}
            borderRadius="5px"
            hasLeftBorder={false}
            mt={0}
          >
            <Text textStyle="bodyFamilyRegular" fontSize="13px">
              Once saved, the expiry date and time cannot be removed
            </Text>
          </InfoBanner>
        )}

        {!hasExistingExpiry && (expiryDate || expiryTime) && (
          <Text
            fontSize="12px"
            color="red"
            textAlign="right"
            cursor="pointer"
            onClick={() => {
              onExpiryDateChange?.('');
              onExpiryTimeChange?.('');
            }}
            _hover={{ opacity: 0.8 }}
          >
            Clear
          </Text>
        )}
      </Box>

    </SectionWrapper>
  );
};

export default PassExpirySection;
